import { getGatewayBaseUrl } from "../config";
import { PosSaleContextGatewayClient } from "../gateway/sale-context-client";
import {
  promotionClaimEvidenceFromSession,
  readPromotionClaimSession,
  setPromotionClaimEvaluation,
} from "../promotion/promotion-claim-session";
import {
  localCartSaleFingerprint,
  setLocalCartQuote,
  type LocalCartDraft,
  type LocalSaleQuote,
} from "./cart-state";
import {
  currentCartRuntimeScopeKey,
  readCurrentCartRuntime,
} from "./current-cart-runtime";
import type { TrustedTerminalIdentity } from "./terminal-state";

function quoteLines(cart: LocalCartDraft) {
  return cart.lines.map((line) => ({
    line_id: line.lineId,
    menu_item_id: line.menuItemId,
    menu_variant_id: line.menuVariantId,
    quantity: line.quantity,
    modifier_option_ids: line.modifiers.map((modifier) => modifier.modifierOptionId),
  }));
}

function currentCart(identity: TrustedTerminalIdentity) {
  const runtime = readCurrentCartRuntime();
  return runtime.scopeKey === currentCartRuntimeScopeKey(identity) ? runtime.cart : null;
}

export async function refreshLocalSaleQuote(input: {
  identity: TrustedTerminalIdentity;
  deviceSecret: string;
  sessionToken: string;
  cart: LocalCartDraft;
  evaluationAt?: string;
}) {
  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) throw new Error("POS_GATEWAY_NOT_CONFIGURED");

  const cart = currentCart(input.identity) ?? input.cart;
  if (!cart.lines.length) return null;
  const requestedRequestKey = cart.requestKey;
  const requestedFingerprint = localCartSaleFingerprint(cart);
  const claimSession = readPromotionClaimSession();
  const requestedClaimRevision = claimSession.revision;
  const promotionClaimEvidence = promotionClaimEvidenceFromSession(claimSession);

  // Customer-bound local-capable claims are already resolved from the in-memory
  // Published Promotion Runtime before this function is called. This path is only
  // canonical background revalidation/readback; it must not repeat Local IndexedDB
  // reads or become a prerequisite for the visible discount.
  const client = new PosSaleContextGatewayClient({ baseUrl });
  // Member Web Claim and STAFF_CLAIM share one customer-selected lane.
  // If a customer-bound Claim is active, stale Staff selections from older builds
  // must not travel to the canonical quote as a second selected candidate.
  const staffClaimPromotionVersionIds = claimSession.customerClaim
    ? []
    : cart.claimedPromotionVersionIds;
  const result = await client.quote({
    deviceSecret: input.deviceSecret,
    sessionToken: input.sessionToken,
    lines: quoteLines(cart),
    claimedPromotionVersionIds: staffClaimPromotionVersionIds,
    promotionClaimEvidence,
    saleContext: cart.selectedMember
      ? { customerId: cart.selectedMember.customerId, membershipId: cart.selectedMember.membershipId }
      : null,
    evaluationAt: input.evaluationAt,
  });
  if (!result.ok || !result.data?.quote) throw new Error(result.error ?? "POS_SALE_QUOTE_FAILED");

  const current = currentCart(input.identity);
  if (
    !current ||
    current.requestKey !== requestedRequestKey ||
    localCartSaleFingerprint(current) !== requestedFingerprint
  ) {
    throw new Error("POS_SALE_QUOTE_CART_CHANGED");
  }
  if (readPromotionClaimSession().revision !== requestedClaimRevision) {
    throw new Error("POS_SALE_CLAIM_CHANGED");
  }

  const quote = result.data.quote;
  const normalized: Omit<LocalSaleQuote, "cartFingerprint"> = {
    quoteFingerprint: quote.quote_fingerprint,
    evaluatedAt: quote.evaluation_at,
    currencyCode: quote.currency_code,
    prePromotionTotal: Number(quote.amounts.pre_promotion_total),
    promotionDiscountTotal: Number(quote.amounts.promotion_discount_total),
    postPromotionTotal: Number(quote.amounts.post_promotion_total),
    appliedPromotions: (quote.promotion_result.applied_promotions ?? []).map((promotion) => ({
      promotionPolicyId: promotion.policy_id,
      promotionVersionId: promotion.promotion_version_id,
      policyCode: promotion.policy_code,
      policyName: promotion.policy_name,
      activationCode: promotion.activation_code,
      appliedDiscountAmount: Number(promotion.applied_discount_amount),
      loyaltyEarnTreatment: promotion.loyalty_earn_treatment ?? "FULL_POINTS",
      conditionDocument: promotion.condition_document,
      benefitDocument: promotion.benefit_document,
      lineAllocations: Array.isArray(promotion.line_allocations)
        ? promotion.line_allocations.map((allocation) => ({
            lineId: allocation.line_id,
            amount: Number(allocation.amount),
            allocationPrecision: "LINE" as const,
          }))
        : [],
      offlineCapability: promotion.offline_capability,
    })),
    promotionRuntimeRevision: undefined,
    quoteSource: "SERVER_COMMERCE_QUOTE_V1",
    requiresOnlineResolution: false,
    staffClaimOptions: (result.data.staff_claim_options ?? []).map((promotion) => ({
      promotionPolicyId: promotion.promotion_policy_id,
      promotionVersionId: promotion.promotion_version_id,
      policyCode: promotion.policy_code,
      policyName: promotion.policy_name,
      offlineCapability: promotion.offline_capability,
    })),
  };

  const evaluation = promotionClaimEvidence.length
    ? quote.promotion_claim_evaluations?.[0] ?? null
    : null;
  setPromotionClaimEvaluation(requestedClaimRevision, evaluation);

  const next = await setLocalCartQuote(input.identity, current, normalized);
  return next.quote;
}
